import { useQueryClient } from "@tanstack/react-query"
import { log } from "helpers/logger"
import { useInfiniteGetQuery } from "hooks/queries"
import { createContext, useCallback, useEffect, useMemo, useRef } from "react"
import { useAccountAlias } from "resources/account"
import { RefreshableError } from "../helpers/errors"
import { useAiTeammateContext, useThreadsContext } from "../hooks/context"
import useSendMessage from "../hooks/send-message"
import { buildMessageList, findLastAssistantMessageId } from "../utils/message-list"

export const MessagesContext = createContext()

const MESSAGES_PER_PAGE = 50

export function MessagesProvider({ children }) {
  const userMessageInputOverrideCallbacks = useRef(new Map())
  const accountAlias = useAccountAlias()
  const { setError } = useAiTeammateContext()
  const { threadId } = useThreadsContext()
  const { mutate: sendMessage, isError: isSendError, isPending: isSendingMessage, variables: sendMessageVariables } = useSendMessage()
  const queryClient = useQueryClient()

  const queryKey = useMemo(() => ["ai-teammate-messages", accountAlias, threadId], [accountAlias, threadId])

  // When a new thread is created, we add the initial messages to the cache. We need to check if this data is already cached to prevent fetching it from the API.
  const hasCachedData = !!queryClient.getQueryData(queryKey)

  const { isFetching, isFetchingNextPage, data, hasNextPage, refetch, fetchNextPage, error: fetchError } = useInfiniteGetQuery({
    url: `/api/${accountAlias}/ai/chat/threads/${threadId}?limit=${MESSAGES_PER_PAGE}`,
    key: queryKey,
    enabled: !!threadId && !hasCachedData,
    staleTime: Infinity,
    getNextPageParam: (lastPage) => lastPage.nextMessageId,
    initialPageParam: undefined,
  })

  const messages = useMemo(() => buildMessageList(data), [data])
  const lastAssistantMessageId = useMemo(() => findLastAssistantMessageId(messages), [messages])
  const hasMessages = useMemo(() => messages.length > 0, [messages])

  useEffect(() => {
    if (fetchError) {
      log(fetchError)
      setError(new RefreshableError("An unexpected error occurred while fetching the messages. Please try refreshing the thread or resetting it."))
    }
  }, [fetchError, setError])

  const sendMessageWrapper = useCallback((messageText) => {
    sendMessage({ messageText, accountAlias, threadId, lastAssistantMessageId })
  }, [sendMessage, accountAlias, threadId, lastAssistantMessageId])

  const registerUserMessageInputOverrideCallback = useCallback((key, callback) => {
    userMessageInputOverrideCallbacks.current.set(key, callback)
    return () => userMessageInputOverrideCallbacks.current.delete(key)
  }, [])

  const dispatchUserMessageInputOverride = useCallback(userMessageInputOverride => {
    userMessageInputOverrideCallbacks.current.forEach(callback => callback(userMessageInputOverride))
  }, [])

  const value = useMemo(() => ({
    registerUserMessageInputOverrideCallback,
    dispatchUserMessageInputOverride,
    fetchNextPage,
    messages,
    hasMessages,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isSendingMessage,
    sendMessage: sendMessageWrapper,
    lastAssistantMessageId,
    refetch,
    // Expose the last user message text from the failed mutation (for retry functionality)
    lastUserMessageText: isSendError ? sendMessageVariables?.messageText : null,
  }), [
    registerUserMessageInputOverrideCallback,
    dispatchUserMessageInputOverride,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    messages,
    hasMessages,
    sendMessageWrapper,
    lastAssistantMessageId,
    isSendError,
    sendMessageVariables,
    isSendingMessage,
    refetch,
  ])

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>
}
